import axios, { AxiosInstance } from 'axios';
import { format, isValid, parseISO } from 'date-fns';
import { apiConfig } from '@/config/apiConfig';
import { getVoiceFallbackClient } from './_shared';
import { fetchVoiceAgents, mapVoiceAgent } from './voiceAgents';
import type { VoiceAgentRow } from './voiceAgents';

// ── Voice workflows list (/voice-workflows) ──────────────────────────

export type VoiceWorkflowRow = {
  id: string;
  name: string;
  nodes: number;
  icon: string;
  status: 'deployed' | 'draft';
  campaigns: number;
  lastEdit: string;
  tags: string[];
  description?: string;
  raw?: any;
};

export type VoiceWorkflowPaletteNode = {
  type: string;
  icon: string;
  label: string;
  desc: string;
  color: string;
};

export type VoiceWorkflowCanvasNode = {
  id: string;
  type: string;
  label: string;
  icon: string;
  x: number;
  y: number;
  branches?: boolean;
  branch?: string;
};

export type VoiceWorkflowsPagePayload = {
  workflows: VoiceWorkflowRow[];
  nodePalette: VoiceWorkflowPaletteNode[];
  sampleCanvasNodes: VoiceWorkflowCanvasNode[];
};

export const VOICE_WORKFLOW_NODE_PALETTE: VoiceWorkflowPaletteNode[] = [
  { type: 'trigger', icon: 'solar:iphone-bold', label: 'QR Scan Trigger', desc: 'Starts the voice session when a QR code is scanned', color: '#1B8187' },
  { type: 'voice', icon: 'solar:microphone-bold', label: 'Voice Turn', desc: 'Agent speaks and listens for a response', color: '#5B4FCF' },
  { type: 'condition', icon: 'solar:shuffle-bold', label: 'Condition', desc: 'Branch based on sentiment, intent, or data', color: '#D4900A' },
  { type: 'rag', icon: 'solar:plain-2-bold', label: 'RAG Lookup', desc: 'Search knowledge base for relevant answers', color: '#2E86AB' },
  { type: 'book', icon: 'solar:calendar-date-bold', label: 'Book Appointment', desc: 'Check calendar availability and book a slot', color: '#27AE60' },
  { type: 'transfer', icon: 'solar:phone-bold', label: 'Warm Transfer', desc: 'Transfer live call to a human agent with whisper', color: '#C0392B' },
  { type: 'sms', icon: 'solar:chat-round-dots-bold', label: 'Send SMS', desc: 'Send confirmation or follow-up text', color: '#1B8187' },
  { type: 'email', icon: 'solar:letter-bold', label: 'Send Email', desc: 'Send email with booking details or ICS file', color: '#5B4FCF' },
  { type: 'signal', icon: 'solar:satellite-bold', label: 'SignalStack Emit', desc: 'Fire an event to the signal pipeline', color: '#D4900A' },
  { type: 'end', icon: 'solar:logout-bold', label: 'End Call', desc: 'Gracefully end the conversation', color: '#7A8C96' },
];

export const VOICE_WORKFLOW_SAMPLE_CANVAS: VoiceWorkflowCanvasNode[] = [
  { id: 'n1', type: 'trigger', label: 'QR Scan Trigger', icon: 'solar:iphone-bold', x: 60, y: 30 },
  { id: 'n2', type: 'voice', label: 'Greeting Turn', icon: 'solar:microphone-bold', x: 60, y: 120 },
  { id: 'n3', type: 'condition', label: 'Engagement Check', icon: 'solar:shuffle-bold', x: 60, y: 210, branches: true },
  { id: 'n4', type: 'rag', label: 'Eligibility Lookup', icon: 'solar:plain-2-bold', x: 20, y: 300, branch: 'yes' },
  { id: 'n5', type: 'voice', label: 'Qualification Turn', icon: 'solar:microphone-bold', x: 20, y: 390 },
  { id: 'n6', type: 'book', label: 'Book Appointment', icon: 'solar:calendar-date-bold', x: 20, y: 480 },
  { id: 'n7', type: 'sms', label: 'Send SMS', icon: 'solar:chat-round-dots-bold', x: 20, y: 570 },
  { id: 'n8', type: 'end', label: 'Graceful Exit', icon: 'solar:logout-bold', x: 120, y: 300, branch: 'no' },
];

const INITIAL_VOICE_WORKFLOWS: VoiceWorkflowRow[] = [
  { id: 'wf1', name: 'HomeServ Qualifier Flow v2', nodes: 8, icon: 'solar:bolt-bold', status: 'deployed', campaigns: 2, lastEdit: 'Apr 12', tags: ['Booking', 'SMS', 'SignalStack'] },
  { id: 'wf2', name: 'PlumbRight Emergency Flow', nodes: 5, icon: 'solar:alarm-bold', status: 'deployed', campaigns: 1, lastEdit: 'Apr 10', tags: ['Warm transfer', 'Urgency'] },
  { id: 'wf3', name: 'CoolAir Spring Flow', nodes: 6, icon: 'solar:snowflake-bold', status: 'draft', campaigns: 0, lastEdit: 'Apr 8', tags: ['Booking', 'Email'] },
  { id: 'wf4', name: 'Insurance Review Flow', nodes: 7, icon: 'solar:shield-check-bold', status: 'draft', campaigns: 0, lastEdit: 'Apr 5', tags: ['TCPA', 'CRM'] },
];

function cloneVoiceWorkflow(w: VoiceWorkflowRow): VoiceWorkflowRow {
  return { ...w, tags: [...w.tags] };
}

let voiceWorkflowsStore: VoiceWorkflowRow[] = INITIAL_VOICE_WORKFLOWS.map(cloneVoiceWorkflow);

/** Map `GET /api/workflows` item → UI row. */
export function mapApiWorkflowToRow(data: any): VoiceWorkflowRow {
  const st = String(data.status || '').toLowerCase();
  const deployed = st.includes('deploy') || st === 'active' || st === 'live';
  const last = data.last_edited_at ? new Date(data.last_edited_at) : null;
  return {
    id: data.id,
    name: data.name || 'Untitled workflow',
    nodes: typeof data.node_count === 'number' ? data.node_count : 0,
    icon: 'solar:bolt-bold',
    status: deployed ? 'deployed' : 'draft',
    campaigns: typeof data.campaigns_using === 'number' ? data.campaigns_using : 0,
    lastEdit: last ? last.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '—',
    tags: [],
  };
}

export async function fetchVoiceWorkflowsPage(axiosInstance?: AxiosInstance): Promise<VoiceWorkflowsPagePayload> {
  const client = axiosInstance || getVoiceFallbackClient();
  const url = apiConfig.voiceEndpoints.workflows;
  const response = await client.get<any[]>(url);
  const workflows = (response.data || []).map(mapApiWorkflowToRow);
  return {
    workflows,
    nodePalette: VOICE_WORKFLOW_NODE_PALETTE.map((p) => ({ ...p })),
    sampleCanvasNodes: VOICE_WORKFLOW_SAMPLE_CANVAS.map((n) => ({ ...n })),
  };
}

/** Published workflow canvas template from `GET /api/workflow-templates`. `canvas` is an opaque node-graph array defined server-side. */
export type VoiceWorkflowTemplateApi = {
  key: string;
  name: string;
  canvas: any[];
  config: Record<string, any>;
};

export async function fetchVoiceWorkflowTemplates(axiosInstance?: AxiosInstance): Promise<VoiceWorkflowTemplateApi[]> {
  const client = axiosInstance || getVoiceFallbackClient();
  const url = apiConfig.voiceEndpoints.workflowTemplates;
  const response = await client.get<VoiceWorkflowTemplateApi[]>(url);
  return response.data || [];
}

export async function fetchVoiceWorkflowTemplate(key: string, axiosInstance?: AxiosInstance): Promise<VoiceWorkflowTemplateApi | null> {
  const client = axiosInstance || getVoiceFallbackClient();
  const url = `${apiConfig.voiceEndpoints.workflowTemplates}/${encodeURIComponent(key)}`;
  const response = await client.get<VoiceWorkflowTemplateApi>(url);
  return response.data ?? null;
}

/** Published agent persona template from `GET /api/agent-templates`. Confirmed
 * live 2026-08-20 — `key` matches this app's campaign_type enum (e.g.
 * 'missed_call_recovery', 'inbound_line', 'reengagement'), same keying as
 * this wizard's local AGENT_TEMPLATES (campaignUseCases.ts). `actions_enabled`
 * is a list of action ids (e.g. 'book_appointment'), not display labels. */
export type AgentTemplateApi = {
  key: string;
  name: string;
  objective: string;
  tone: string[];
  tone_locked: boolean;
  actions_enabled: string[];
  greeting: string;
  system_prompt_seed: string;
  /** True for a workspace-created custom template (from POST /api/agent-templates), as opposed to a platform canonical/stock one — distinguishes what's safe to offer for delete. */
  is_custom?: boolean;
};

export async function fetchAgentTemplates(axiosInstance?: AxiosInstance): Promise<AgentTemplateApi[]> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.get<AgentTemplateApi[]>(apiConfig.voiceEndpoints.agentTemplates);
  return response.data || [];
}

export async function fetchAgentTemplate(key: string, axiosInstance?: AxiosInstance): Promise<AgentTemplateApi | null> {
  const client = axiosInstance || getVoiceFallbackClient();
  const url = `${apiConfig.voiceEndpoints.agentTemplates}/${encodeURIComponent(key)}`;
  const response = await client.get<AgentTemplateApi>(url);
  return response.data ?? null;
}

/** Matches the real `AgentTemplateCreate` request schema — note `tone_locked` is
 * NOT accepted here (it only appears on the response); the backend decides it
 * for custom templates, so it can't be set at creation time. */
export interface CreateAgentTemplateInput {
  name: string;
  objective: string;
  tone?: string[];
  actions_enabled?: string[];
  greeting: string;
  system_prompt_seed: string;
}

/** `POST /api/agent-templates` — create a workspace-scoped custom agent template. */
export async function createAgentTemplate(input: CreateAgentTemplateInput, axiosInstance?: AxiosInstance): Promise<AgentTemplateApi> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.post<AgentTemplateApi>(apiConfig.voiceEndpoints.agentTemplates, input);
  return response.data;
}

/** `DELETE /api/agent-templates/{key}` — remove a workspace-scoped custom agent template. */
export async function deleteAgentTemplate(key: string, axiosInstance?: AxiosInstance): Promise<boolean> {
  const client = axiosInstance || getVoiceFallbackClient();
  const url = `${apiConfig.voiceEndpoints.agentTemplates}/${encodeURIComponent(key)}`;
  const response = await client.delete(url);
  return response.status === 204 || response.status === 200;
}

export interface DeployVoiceWorkflowResult {
  workflow: VoiceWorkflowRow | null;
  /** Warning-level v13 rule-engine issues on an otherwise-successful deploy (e.g. a template modified from its canonical version) — non-blocking, but worth surfacing. */
  warnings: any[];
}

export async function deployVoiceWorkflow(id: string, axiosInstance?: AxiosInstance): Promise<DeployVoiceWorkflowResult> {
  const client = axiosInstance || getVoiceFallbackClient();
  const url = `${apiConfig.voiceEndpoints.workflows}/${id}/deploy`;
  const response = await client.post<any>(url, {});
  return {
    workflow: mapApiWorkflowToRow(response.data),
    warnings: response.data?.deploy_warnings || [],
  };
}

/** `GET /api/agents/{id}` — returns the agent's full config (system_prompt,
 * voice, greeting) which the list endpoint (`fetchVoiceAgents`) may omit. */
export async function fetchVoiceAgent(id: string, axiosInstance?: AxiosInstance): Promise<VoiceAgentRow | null> {
  const client = axiosInstance || getVoiceFallbackClient();
  const url = `${apiConfig.voiceEndpoints.agents}/${id}`;
  const response = await client.get<any>(url);
  return response.data ? mapVoiceAgent(response.data) : null;
}

export async function fetchVoiceWorkflow(id: string, axiosInstance?: AxiosInstance): Promise<any | null> {
  const client = axiosInstance || getVoiceFallbackClient();
  const url = `${apiConfig.voiceEndpoints.workflows}/${id}`;
  const response = await client.get<any>(url);
  return response.data;
}

export async function createVoiceWorkflowDraft(input: { name?: string; canvas?: any[]; config?: any } = {}, axiosInstance?: AxiosInstance): Promise<VoiceWorkflowRow> {
  const client = axiosInstance || getVoiceFallbackClient();
  const url = apiConfig.voiceEndpoints.workflows;
  const response = await client.post<any>(url, {
    name: input.name || 'New workflow',
    description: null,
    canvas: input.canvas || [],
    config: input.config || {},
  });
  return mapApiWorkflowToRow(response.data);
}

export async function updateVoiceWorkflow(id: string, patch: { name?: string; canvas?: any[]; config?: any }, axiosInstance?: AxiosInstance): Promise<VoiceWorkflowRow | null> {
  const client = axiosInstance || getVoiceFallbackClient();
  const url = `${apiConfig.voiceEndpoints.workflows}/${id}`;
  const response = await client.put<any>(url, patch);
  return mapApiWorkflowToRow(response.data);
}

export async function deleteVoiceWorkflow(id: string, axiosInstance?: AxiosInstance): Promise<boolean> {
  const client = axiosInstance || getVoiceFallbackClient();
  const url = `${apiConfig.voiceEndpoints.workflows}/${id}`;
  const response = await client.delete(url);
  return response.status === 204 || response.status === 200;
}

/** `routes/workflows.py`'s real StructuralValidationResponse shape — an object
 * with two separate arrays, NOT a bare array. v13_issues come from the cross-
 * entity rule engine (run_validation); structural_issues come from the W1-W7
 * graph checks (validate_workflow_structure) — the same two checks deploy_
 * workflow itself enforces (as detail.blockers / detail.structural_blockers
 * on a 409), so both must be surfaced here or Validate can show "all clear"
 * on a workflow Deploy will legitimately reject. */
export interface WorkflowV13Issue {
  level: 'blocker' | 'warning' | 'suggestion';
  icon: string;
  category: string;
  title: string;
  detail: string;
  fix: string;
  affected: string[];
  fixable: boolean;
  fixLabel?: string;
  fixFn?: string;
  fix_action?: { action: string; [key: string]: any };
}

export interface WorkflowStructuralIssue {
  rule: string;
  severity: 'critical' | 'warning';
  message: string;
  node_id?: string | null;
}

export interface WorkflowValidationResult {
  v13_issues: WorkflowV13Issue[];
  structural_issues: WorkflowStructuralIssue[];
}

export async function validateVoiceWorkflow(id: string, axiosInstance?: AxiosInstance): Promise<WorkflowValidationResult> {
  const client = axiosInstance || getVoiceFallbackClient();
  const url = `${apiConfig.voiceEndpoints.workflows}/${id}/validate`;
  const response = await client.post<WorkflowValidationResult>(url, {});
  return response.data;
}

// Matches the real backend contract (routes/workflows.py DryRunRequest) — the
// simulator is a deterministic graph walker, not an LLM, so there is no
// agent-generated reply text to show; only what was actually computed.
export interface WorkflowTestPayload {
  initial_message?: string;
  caller_phone?: string;
  persona?: 'neutral' | 'interested' | 'skeptical' | 'busy';
}

export interface WorkflowTestTurn {
  turn_number: number;
  utterance: string;
  cursor_position: string | null;
  conditions_evaluated: Record<string, any>[];
  tools_triggered: string[];
  context_vars: Record<string, any>;
}

export interface WorkflowTestResult {
  turns: WorkflowTestTurn[];
  final_outcome: string;
  path: string[];
}

export interface NodeTurnTrace {
  turn_number: number;
  utterance: string;
  cursor_position: string | null;
  conditions_evaluated: Record<string, any>[];
  tools_triggered: string[];
  context_vars: Record<string, any>;
}

export interface NodeTestResult {
  turns: NodeTurnTrace[];
  final_outcome: string;
  path: string[];
}

/** `POST /api/workflows/{id}/test` — real single-node test: starts the dry-run
 * simulator AT this node (start_node_id) with the node's test-input JSON as
 * initial context_vars, instead of always walking from the workflow entry. */
export async function runNodeTest(
  workflowId: string,
  nodeId: string,
  testInput: Record<string, any>,
  axiosInstance?: AxiosInstance
): Promise<NodeTestResult> {
  const client = axiosInstance || getVoiceFallbackClient();
  const url = `${apiConfig.voiceEndpoints.workflows}/${workflowId}/test`;
  const response = await client.post<NodeTestResult>(url, {
    utterances: ['(node test)'],
    start_node_id: nodeId,
    context_vars: testInput,
  });
  return response.data;
}

/** `POST /api/workflows/{id}/test` — run the real dry-run simulator from the
 * workflow's entry node. caller_phone/persona are passed as context_vars
 * (the deterministic simulator has no LLM, so persona only affects condition
 * evaluation, not generated dialogue). */
export async function testVoiceWorkflow(
  workflowId: string,
  payload: WorkflowTestPayload,
  axiosInstance?: AxiosInstance
): Promise<WorkflowTestResult> {
  const client = axiosInstance || getVoiceFallbackClient();
  const url = `${apiConfig.voiceEndpoints.workflows}/${workflowId}/test`;
  const response = await client.post<WorkflowTestResult>(url, {
    utterances: [payload.initial_message?.trim() || 'Hello'],
    context_vars: {
      ...(payload.caller_phone ? { caller_phone: payload.caller_phone } : {}),
      ...(payload.persona ? { persona: payload.persona } : {}),
    },
  });
  return response.data;
}

export interface WorkflowScenario {
  id: string;
  workflow_id: string;
  name: string;
  input_utterances: string[];
  expected_outcome?: string | null;
  expected_tool_calls: string[];
  created_by?: string | null;
  created_at?: string;
}

export interface WorkflowScenarioCreate {
  name: string;
  input_utterances: string[];
  expected_outcome?: string;
  expected_tool_calls?: string[];
}

export interface WorkflowDryRunResult {
  result: string;
  path_taken: string[];
  steps_executed: number;
  variables: Record<string, any>;
}

export interface WorkflowScenarioRunResult {
  scenario: WorkflowScenario;
  trace: WorkflowTestResult;
  outcome_match: boolean;
  tool_call_match: boolean;
  mismatches: string[];
}

export interface WorkflowScenarioResultSummary {
  scenario_id: string;
  name: string;
  passed: boolean;
  error?: string;
  expected_outcome?: string | null;
  actual_outcome?: string;
  outcome_match?: boolean;
  expected_tool_calls?: string[];
  actual_tool_calls?: string[];
  tool_call_match?: boolean;
  path?: string[];
  structural_issues?: Array<{ rule: string; message: string; severity: string }>;
}

export interface WorkflowTestResultAggregated {
  workflow_id: string;
  total_scenarios: number;
  passed: number;
  failed: number;
  pass_rate: number;
  results: WorkflowScenarioResultSummary[];
}

/** Dry run a workflow with input variables — calls the real dry-run endpoint
 * (`/test/dry-run` doesn't exist on the backend; the real route is `/test`)
 * and translates its {turns, final_outcome, path} response into this
 * function's existing {result, path_taken, steps_executed, variables} shape. */
export async function dryRunWorkflow(
  workflowId: string,
  variables: Record<string, any> = {},
  axiosInstance?: AxiosInstance,
  utterances: string[] = ['(dry run)']
): Promise<WorkflowDryRunResult> {
  const client = axiosInstance || getVoiceFallbackClient();
  const url = `${apiConfig.voiceEndpoints.workflows}/${workflowId}/test`;
  const response = await client.post<WorkflowTestResult>(url, {
    utterances: utterances.length > 0 ? utterances : ['(dry run)'],
    context_vars: variables,
  });
  const { turns, final_outcome, path } = response.data;
  const lastTurn = turns[turns.length - 1];
  return {
    result: final_outcome,
    path_taken: path,
    steps_executed: turns.length,
    variables: lastTurn?.context_vars ?? variables,
  };
}

/** `POST /api/workflows/{id}/test-scenarios` — create a testing scenario for a workflow. */
export async function createWorkflowScenario(
  workflowId: string,
  scenario: WorkflowScenarioCreate,
  axiosInstance?: AxiosInstance
): Promise<WorkflowScenario> {
  const client = axiosInstance || getVoiceFallbackClient();
  const url = `${apiConfig.voiceEndpoints.workflows}/${workflowId}/test-scenarios`;
  const response = await client.post<WorkflowScenario>(url, scenario);
  return response.data;
}

/** `GET /api/workflows/{id}/test-scenarios` — fetch all testing scenarios for a workflow. */
export async function fetchWorkflowScenarios(
  workflowId: string,
  axiosInstance?: AxiosInstance
): Promise<WorkflowScenario[]> {
  const client = axiosInstance || getVoiceFallbackClient();
  const url = `${apiConfig.voiceEndpoints.workflows}/${workflowId}/test-scenarios`;
  const response = await client.get<WorkflowScenario[]>(url);
  return response.data || [];
}

/** `POST /api/workflows/{id}/test-scenarios/{scenarioId}/run` — run a specific testing scenario. */
export async function runWorkflowScenario(
  workflowId: string,
  scenarioId: string,
  axiosInstance?: AxiosInstance
): Promise<WorkflowScenarioRunResult> {
  const client = axiosInstance || getVoiceFallbackClient();
  const url = `${apiConfig.voiceEndpoints.workflows}/${workflowId}/test-scenarios/${scenarioId}/run`;
  const response = await client.post<WorkflowScenarioRunResult>(url, {});
  return response.data;
}

/** `DELETE /api/workflows/{id}/test-scenarios/{scenarioId}` — delete a testing scenario. */
export async function deleteWorkflowScenario(
  workflowId: string,
  scenarioId: string,
  axiosInstance?: AxiosInstance
): Promise<boolean> {
  const client = axiosInstance || getVoiceFallbackClient();
  const url = `${apiConfig.voiceEndpoints.workflows}/${workflowId}/test-scenarios/${scenarioId}`;
  const response = await client.delete(url);
  return response.status === 200 || response.status === 204;
}

/** `GET /api/workflows/{id}/test-results` — fetch aggregated testing results across all scenarios. */
export async function fetchWorkflowTestResults(
  workflowId: string,
  axiosInstance?: AxiosInstance
): Promise<WorkflowTestResultAggregated> {
  const client = axiosInstance || getVoiceFallbackClient();
  const url = `${apiConfig.voiceEndpoints.workflows}/${workflowId}/test-results`;
  const response = await client.get<WorkflowTestResultAggregated>(url);
  return response.data;
}

export interface ValidationCheck {
  rule: string;
  passed: boolean;
  message: string;
  severity: 'critical' | 'warning';
}

export interface ValidationResult {
  valid: boolean;
  checks: ValidationCheck[];
}

export async function validateVoiceCampaign(id: string, axiosInstance?: AxiosInstance): Promise<ValidationResult> {
  const client = axiosInstance || getVoiceFallbackClient();
  const url = `/api/campaigns/${id}/validate`;
  const response = await client.post<any>(url, {});
  const data = response.data;
  // Handle both new { valid, checks } format and legacy array format
  if (Array.isArray(data)) {
    const checks: ValidationCheck[] = data.map((issue: any) => ({
      rule: issue.rule ?? issue.title ?? 'unknown',
      passed: false,
      message: issue.detail ?? issue.message ?? String(issue),
      severity: 'critical',
    }));
    return { valid: checks.length === 0, checks };
  }
  // Backend field names (ready_to_launch/check) differ from this type's (valid/rule).
  const checks: ValidationCheck[] = (data.checks || []).map((c: any) => ({
    rule: c.rule ?? c.check ?? 'unknown',
    passed: !!c.passed,
    message: c.message ?? '',
    severity: c.severity === 'warning' ? 'warning' : 'critical',
  }));
  return { valid: !!data.ready_to_launch, checks };
}

/** BOOSTT-3206: the Section 3 attestation checklist a customer certifies at campaign activation. */
export interface CampaignCertificationAttestations {
  attests_consent_and_legal_right: boolean;
  attests_pewc_held: boolean;
  attests_self_scrubbed: boolean;
  attests_honors_opt_outs: boolean;
  attests_calling_hours_compliant: boolean;
  attests_maintains_consent_records: boolean;
  attests_info_true_and_authorized: boolean;
}

export interface CampaignCertification extends CampaignCertificationAttestations {
  id: string;
  campaign_id: string;
  certifier_name: string;
  certifier_title: string;
  certified_at: string;
  pewc_required: boolean;
}

export async function submitCampaignCertification(
  campaignId: string,
  certifierName: string,
  certifierTitle: string,
  attestations: CampaignCertificationAttestations,
  axiosInstance?: AxiosInstance,
): Promise<CampaignCertification> {
  const client = axiosInstance || getVoiceFallbackClient();
  const url = `/api/campaigns/${campaignId}/certification`;
  const response = await client.post<CampaignCertification>(url, {
    certifier_name: certifierName,
    certifier_title: certifierTitle,
    ...attestations,
  });
  return response.data;
}

export async function fetchCampaignCertification(
  campaignId: string,
  axiosInstance?: AxiosInstance,
): Promise<CampaignCertification | null> {
  const client = axiosInstance || getVoiceFallbackClient();
  const url = `/api/campaigns/${campaignId}/certification`;
  const response = await client.get<CampaignCertification | null>(url);
  return response.data ?? null;
}

/** BOOSTT-3149: Salesforce CRM audience sourcing — connection is workspace-scoped, import is per-campaign. */
export interface CrmConnection {
  id: string;
  provider: 'salesforce';
  status: 'pending_auth' | 'connected' | 'error';
  connected_at: string | null;
  last_error: string | null;
}

export interface CrmObjectSummary {
  name: string;
  label: string;
}

export interface CrmFieldSummary {
  name: string;
  label: string;
  type: string;
}

export interface CrmFieldMapping {
  source_column: string;
  standard_field: string;
}

export async function startSalesforceConnection(name: string, axiosInstance?: AxiosInstance): Promise<{ auth_url: string }> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.post<{ auth_url: string }>('/api/workspaces/current/crm-connections/salesforce', { name });
  return response.data;
}

export async function fetchCrmConnections(axiosInstance?: AxiosInstance): Promise<CrmConnection[]> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.get<{ connections: CrmConnection[] }>('/api/workspaces/current/crm-connections');
  return response.data.connections;
}

export async function confirmCrmConnection(connectionId: string, axiosInstance?: AxiosInstance): Promise<CrmConnection> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.post<CrmConnection>(`/api/workspaces/current/crm-connections/${connectionId}/confirm`);
  return response.data;
}

export async function disconnectCrmConnection(connectionId: string, axiosInstance?: AxiosInstance): Promise<void> {
  const client = axiosInstance || getVoiceFallbackClient();
  await client.delete(`/api/workspaces/current/crm-connections/${connectionId}`);
}

export async function fetchCrmObjects(connectionId: string, axiosInstance?: AxiosInstance): Promise<CrmObjectSummary[]> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.get<{ objects: CrmObjectSummary[] }>(`/api/workspaces/current/crm-connections/${connectionId}/objects`);
  return response.data.objects;
}

export async function fetchCrmObjectFields(connectionId: string, objectName: string, axiosInstance?: AxiosInstance): Promise<CrmFieldSummary[]> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.get<{ fields: CrmFieldSummary[] }>(`/api/workspaces/current/crm-connections/${connectionId}/objects/${objectName}/fields`);
  return response.data.fields;
}

export async function previewCrmObject(connectionId: string, objectName: string, limit = 5, axiosInstance?: AxiosInstance): Promise<{ items: Record<string, unknown>[] }> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.get<{ items: Record<string, unknown>[] }>(
    `/api/workspaces/current/crm-connections/${connectionId}/objects/${objectName}/preview`,
    { params: { limit } },
  );
  return response.data;
}

export async function startCrmAudienceImport(
  campaignId: string,
  connectionId: string,
  objectName: string,
  fieldMappings: CrmFieldMapping[],
  soqlWhere?: string,
  axiosInstance?: AxiosInstance,
): Promise<{ job_id: string }> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.post<{ job_id: string }>(`/api/campaigns/${campaignId}/audience-crm/import`, {
    connection_id: connectionId, object_name: objectName, field_mappings: fieldMappings, soql_where: soqlWhere || null,
  });
  return response.data;
}

export interface CrmImportStatus {
  status: 'queued' | 'running' | 'completed' | 'failed';
  records_imported?: number;
  records_dnc_blocked?: number;
  error?: string;
}

export async function fetchCrmAudienceImportStatus(campaignId: string, axiosInstance?: AxiosInstance): Promise<CrmImportStatus> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.get<CrmImportStatus>(`/api/campaigns/${campaignId}/audience-crm/import/status`);
  return response.data;
}

export interface CapacityEstimate {
  estimated_peak_concurrent: number;
  zone: 'normal' | 'burst' | 'over_capacity';
  subscription_limit: number;
  burst_ceiling: number;
  estimated_cost_usd: number;
  recommendation: string;
}

export interface CapacityStatus {
  provider: string;
  active: number;
  limit: number;
  reserve_buffer: number;
  soft_cap: number;
  queue_depth: number;
  callbacks_pending: number;
  circuit: string;
  per_campaign: Record<string, unknown>[];
}

/** `GET /api/voice/capacity` (API Reference v1 §13) — the same live state the
 * backend's Capacity Manager reads for P0-P3 admission. Per the endpoint's own
 * description: `circuit` is always "closed" (no ElevenLabs-API circuit breaker
 * exists in this codebase — a resilience concept distinct from session-count
 * capacity) and `reserve_buffer` is always 0 (no separate reserved-headroom
 * concept beyond the normal/burst boundary `soft_cap` already reflects). Both
 * are reported honestly as fixed values rather than fabricated here. */
export async function fetchVoiceCapacityStatus(
  campaignId: string,
  axiosInstance?: AxiosInstance
): Promise<CapacityStatus> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.get<CapacityStatus>('/api/voice/capacity', {
    params: { campaign_id: campaignId },
  });
  return response.data;
}

/** Frontend Integration F2 — filters accepted by `GET /api/voice/sessions`
 * (confirmed via openapi.json); `status`/`channel`/`outcome` are plain string
 * query params server-side, no enum declared. */
export type VoiceSessionListFilter = {
  campaign_id?: string;
  status?: string;
  channel?: string;
  outcome?: string;
  limit?: number;
  offset?: number;
};

/** A voice session row as returned by `GET /api/voice/sessions`. Confirmed
 * against a real staging response (2026-08-21) — the backend still declares
 * no response schema in openapi.json, but the fields below were verified.
 * There is no `session_id` field; `id` is the real key. `status` has only
 * been observed as `"completed"` so far — active/queued live-call values are
 * still unconfirmed. */
export type VoiceSessionListItem = {
  id?: string;
  campaign_id?: string;
  campaign_name?: string;
  agent_name?: string;
  status?: string;
  channel?: string;
  outcome?: string;
  duration_seconds?: number;
  caller_phone?: string | null;
  caller_name?: string;
  title?: string;
  sentiment_score?: number | null;
  started_at?: string;
  ended_at?: string;
  transfer_status?: string | null;
  transfer_started_at?: string | null;
} & Record<string, unknown>;

/** `GET /api/voice/sessions` — list voice sessions with optional filters
 * (Frontend Integration F2, voice-supervisor live monitoring). Response shape
 * confirmed against real staging data (see `VoiceSessionListItem`); still no
 * declared schema in openapi.json, so keep reading fields defensively. */
export async function fetchVoiceSessions(
  filter: VoiceSessionListFilter = {},
  axiosInstance?: AxiosInstance
): Promise<VoiceSessionListItem[]> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.get<any>(`${apiConfig.voiceEndpoints.voice}/sessions`, { params: filter });
  return Array.isArray(response.data) ? response.data : response.data?.sessions ?? response.data?.items ?? [];
}

export interface BurstNotification {
  id: string;
  campaign_id: string;
  zone: 'normal' | 'burst' | 'over_capacity';
  concurrent_calls: number;
  subscription_limit: number;
  burst_ceiling: number;
  burst_cost_usd: number;
  message?: string;
  created_at: string;
}

/** `GET /api/campaigns/{id}/burst-notifications` — active burst alerts for a running campaign. */
export async function fetchBurstNotifications(
  campaignId: string,
  axiosInstance?: AxiosInstance
): Promise<BurstNotification[]> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.get<any>(`/api/campaigns/${campaignId}/burst-notifications`);
  return Array.isArray(response.data) ? response.data : response.data?.notifications ?? [];
}

/** Backend expects `mail_volume`/`scan_rate_pct` (required) — not
 * `audience_size`/`response_rate_pct`. Confirmed live: the backend now
 * returns the workspace's REAL plan-derived subscription_limit/burst_ceiling
 * (routes/campaigns.py's estimate_capacity reads capacity_service.plan_limits(),
 * the same source the actual live-traffic admission gate uses) and a real
 * dollar figure priced off routes/billing.py's TIER_PRICES
 * (cost_estimate.total_usd_est) — no more hardcoded 50/150 or a flat
 * credits->USD guess on either side of this call. */
export async function estimateCampaignCapacity(
  id: string,
  payload: { audience_size: number; response_rate_pct: number },
  axiosInstance?: AxiosInstance
): Promise<CapacityEstimate> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.post<any>(`/api/campaigns/${id}/estimate-capacity`, {
    mail_volume: payload.audience_size,
    scan_rate_pct: payload.response_rate_pct,
  });
  const d = response.data ?? {};
  const zone = d.zone ?? 'normal';
  const recommendation =
    zone === 'over_capacity'
      ? 'Audience exceeds capacity limits. Reduce audience size or upgrade your plan.'
      : zone === 'burst'
      ? 'This mailing will trigger burst pricing (2× rate) during peak hours.'
      : 'Capacity is within normal limits.';
  return {
    estimated_peak_concurrent: d.peak_concurrent ?? 0,
    zone,
    subscription_limit: d.subscription_limit ?? 0,
    burst_ceiling: d.burst_ceiling ?? 0,
    estimated_cost_usd: d.cost_estimate?.total_usd_est ?? 0,
    recommendation,
  };
}

export interface PostCallData {
  sentiment_trend: number[];
  slots_captured: Record<string, any>;
  tools_called: Array<{
    tool: string;
    result: string;
    turn: number;
    latency_ms: number;
  }>;
  outcome_confidence: number;
}

export interface PostCallAnalytics {
  outcome_distribution: Record<string, number>;
  avg_sentiment: number;
  avg_duration_seconds: number;
  top_objections: Array<{ objection: string; count: number }>;
  top_intents: Array<{ intent: string; count: number }>;
  total_sessions: number;
}

export interface BurstCostEstimation {
  normal_sessions: number;
  burst_sessions: number;
  /** Transferred to a human once burst_ceiling is hit -- never dropped (2026-09-09 decision). */
  over_capacity_transferred: number;
  normal_cost_usd: number;
  burst_cost_usd: number;
  total_estimated_usd: number;
  rates: {
    normal_rate_per_min: number;
    burst_rate_per_min: number;
  };
}

/** `GET /api/voice/sessions/{id}/post-call` — retrieve detailed post-call statistics/data. */
export async function fetchPostCallData(
  sessionId: string,
  axiosInstance?: AxiosInstance
): Promise<PostCallData> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.get<PostCallData>(`/api/voice/sessions/${sessionId}/post-call`);
  return response.data;
}

/** `GET /api/analytics/post-call` — retrieve aggregated post-call analytics metrics. */
export async function fetchPostCallAnalytics(
  params: { campaign_id?: string; days?: number },
  axiosInstance?: AxiosInstance
): Promise<PostCallAnalytics> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.get<PostCallAnalytics>(`/api/analytics/post-call`, { params });
  return response.data;
}

/** `GET /api/webhooks/outbound-config` — retrieve outbound webhook config for a campaign. */
export async function fetchOutboundWebhookConfig(
  campaignId: string,
  axiosInstance?: AxiosInstance
): Promise<{ webhook_url: string; webhook_secret: string } | null> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.get<any>(`/api/webhooks/outbound-config`, {
    params: { campaign_id: campaignId }
  });
  return response.data;
}

/** `POST /api/webhooks/outbound-config` — configure outbound webhook url and secret. */
export async function saveOutboundWebhookConfig(
  campaignId: string,
  webhookUrl: string,
  webhookSecret: string,
  axiosInstance?: AxiosInstance
): Promise<{ ok: boolean }> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.post<{ ok: boolean }>(`/api/webhooks/outbound-config`, {
    campaign_id: campaignId,
    webhook_url: webhookUrl,
    webhook_secret: webhookSecret
  });
  return response.data;
}

// ── Workflow extras ───────────────────────────────────────────────────

/** `POST /api/workflows/{workflow_id}/duplicate` — duplicate a workflow. */
export async function duplicateVoiceWorkflow(id: string, axiosInstance?: AxiosInstance): Promise<VoiceWorkflowRow> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.post<any>(`${apiConfig.voiceEndpoints.workflows}/${id}/duplicate`, {});
  return mapApiWorkflowToRow(response.data);
}

export type WorkflowValidationSummary = { errors: number; warnings: number; info: number };

/** `GET /api/workflows/{workflow_id}/validation-summary` — get validation badge counts. */
export async function fetchWorkflowValidationSummary(id: string, axiosInstance?: AxiosInstance): Promise<WorkflowValidationSummary> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.get<WorkflowValidationSummary>(`${apiConfig.voiceEndpoints.workflows}/${id}/validation-summary`);
  return response.data;
}
